import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { PButtonComponent } from '../../../../../shared/design-system/atoms/p-button/p-button.component';
import { PInputComponent } from '../../../../../shared/design-system/atoms/p-input/p-input.component';
import { PSelectItem } from '../../../../../shared/design-system/atoms/p-select/p-select-item.model';
import { PSelectComponent } from '../../../../../shared/design-system/atoms/p-select/p-select.component';
import { IconsModule } from '../../../../../shared/design-system/icons/icons.module';
import countriesData from '../../../../../shared/data/temporary_country_list.json';
import { IndustryTypeEnum } from '../../../../../shared/models/industry-type.enum';
import { PervalValidators } from '../../../../../shared/validators/perval-validators';
import { SignUpCompanyData } from '../../interfaces/sign-up-store.interface';
import { MON_ENTREPRISE_ERROR_MESSAGES } from '../../../../../shared/errors/forms-errors';
import { getFirstControlErrorMessage } from '../../../../../shared/utils/forms.utils';
import { Siret } from '../../../../models/siret.model';
import { CountryValueEnum } from '../../../../../shared/models/country-value.enum';
import { PErrorNotificationComponent } from '../../../../../shared/design-system/molecules/p-error-notification/p-error-notification.component';
import { DesignSystemModule } from '../../../../../shared/design-system/design-system.module';
import { FilterLabelsPipe } from '../../../../../shared/pipes/filter-labels.pipe';
import formeJuridiquesData from '../../../../../../assets/json/formesJuridiques.json';
import { FormeJuridique } from '../../../../models/forme-juridique.model';
import { findCountryByLabel } from '../../../../mappers/sign-up-request.mapper';

@Component({
  selector: 'app-mon-entreprise-form',
  standalone: true,
  imports: [
    PButtonComponent,
    CommonModule,
    IconsModule,
    PInputComponent,
    ReactiveFormsModule,
    PSelectComponent,
    PErrorNotificationComponent,
    DesignSystemModule,
    FilterLabelsPipe,
  ],
  templateUrl: './mon-entreprise-form.component.html',
  styleUrl: './mon-entreprise-form.component.scss',
})
export class MonEntrepriseFormComponent
  implements OnInit, OnChanges, OnDestroy
{
  private readonly DEBOUNCE_TIME_TO_SHARE_FORM_IN_MS = 150;
  private readonly destroy$ = new Subject<void>();
  private readonly GATEWAY_ERROR_STATUS_NUMBER = 502;

  @Input() label!: string;
  @Input() initialValue!: SignUpCompanyData;
  @Input() siret?: Siret | null;
  @Input() siretErrorMessage?: string | null;
  @Input() siretErrorStatus?: number | null;

  @Output() companyChanged = new EventEmitter<SignUpCompanyData>();
  @Output() verifySiret = new EventEmitter<string>();
  @Output() disableNextButton = new EventEmitter<boolean>();
  @Output() cleanSiretError = new EventEmitter();
  @Output() notificationClick = new EventEmitter();

  readonly countryValue = CountryValueEnum;
  readonly industryType = IndustryTypeEnum;

  countries: PSelectItem[] = countriesData.map((c) => ({
    label: c.pays,
    value: c.pays,
  }));
  selectedCountry: PSelectItem = this.countries[0];
  industryTypeItems: PSelectItem[] = Object.values(IndustryTypeEnum).map(
    (v) => ({ value: v, label: v }),
  );
  selectedIndustryType: PSelectItem = { value: null, label: null };
  readonly formesJuridiques: FormeJuridique[] =
    formeJuridiquesData.formesJuridiques;
  readonly formesJuridiquesLabels: string[] =
    formeJuridiquesData.formesJuridiques.map((item) => item.label);
  isSiretVerified = false;
  isNameEditable = false;

  form = new FormGroup({
    country: new FormControl(countriesData[0].pays, {
      validators: [Validators.required],
    }),

    vatNumber: new FormControl('', {
      validators: [
        PervalValidators.requiredIf((formValue) => {
          const code = findCountryByLabel(formValue.country)?.country;
          return code === CountryValueEnum.FR || code === CountryValueEnum.UE;
        }),
        PervalValidators.vatNumber,
      ],
    }),

    siret: new FormControl('', {
      validators: [
        PervalValidators.requiredIf(
          (formValue) =>
            findCountryByLabel(formValue.country)?.country ===
            CountryValueEnum.FR,
        ),
        PervalValidators.siret,
      ],
    }),

    industryType: new FormControl('', {
      validators: [Validators.required],
    }),

    legalStatus: new FormControl('', {
      validators: [
        Validators.required,
        PervalValidators.oneOf(this.formesJuridiquesLabels),
      ],
    }),

    name: new FormControl('', {
      validators: [Validators.required, Validators.maxLength(255)],
    }),

    address1: new FormControl('', {
      validators: [Validators.required, Validators.maxLength(255)],
    }),

    address2: new FormControl('', {
      validators: [Validators.maxLength(255)],
    }),

    address3: new FormControl('', {
      validators: [Validators.maxLength(255)],
    }),

    city: new FormControl('', {
      validators: [Validators.required, Validators.maxLength(100)],
    }),

    postalCode: new FormControl('', {
      validators: [Validators.required, Validators.maxLength(10)],
    }),

    phoneNumber: new FormControl('', {
      validators: [PervalValidators.worldPhoneNumber],
    }),
  });

  get selectedCountryCode(): string {
    return (
      countriesData.find((c) => c.pays === this.selectedCountry.label)
        ?.country ?? ''
    );
  }

  ngOnInit(): void {
    this.patchFormFromInitialValue();
    this.subscribeToFormChanges();
    this.emitDisableNextButton();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.handleSiretChange(changes);
    this.handleSiretErrorStatusChange(changes);
  }

  ngOnDestroy(): void {
    this.disableNextButton.emit(false);
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onCountrySelected(country: PSelectItem): void {
    this.selectedCountry = country;
    this.form.controls.country.setValue(country.label ?? '');

    if (this.selectedCountryCode !== this.countryValue.FR) {
      this.clearSiretField();
      this.onSiretInputChange();
      if (this.siretErrorMessage || this.siretErrorStatus) {
        this.cleanSiretError.emit();
      }
    }
    if (this.selectedCountryCode === this.countryValue.XX) {
      this.clearVatNumberField();
    }
  }

  public getErrorMessage(
    controlName: keyof typeof MON_ENTREPRISE_ERROR_MESSAGES,
  ): string {
    const control = this.form.controls[controlName];

    if (!control.touched) {
      return '';
    }

    return getFirstControlErrorMessage(
      this.form.controls[controlName],
      MON_ENTREPRISE_ERROR_MESSAGES[controlName],
    );
  }

  public onVerifySiret(): void {
    this.siretErrorStatus = null;
    if (this.form.controls.siret.valid && !this.isSiretVerified) {
      this.verifySiret.emit(this.form.controls.siret.value as string);
    }
  }

  public onSiretInputChange(): void {
    this.resetAutofilledCompanyFields();
    this.isSiretVerified = false;
  }

  public onNotificationClick() {
    this.notificationClick.emit();
  }

  public onIndustryTypeSelected(item: PSelectItem): void {
    this.selectedIndustryType = item;
    this.form.controls.industryType.setValue(item.value ?? '');
  }

  public onIndustryTypeBlur(): void {
    this.form.controls.industryType.markAsTouched();
  }

  public onLegalStatusSelected(label: string): void {
    this.form.controls.legalStatus.setValue(label);
  }

  public onLegalStatusBlur(): void {
    this.form.controls.legalStatus.markAsTouched();
  }

  private patchFormFromInitialValue(): void {
    if (!this.initialValue) {
      return;
    }

    this.form.patchValue(this.initialValue, { emitEvent: false });
    this.selectedCountry = this.getSelectedCountryFromInitialValue();
    this.selectedIndustryType = this.getSelectedIndustryTypeFromInitialValue();
    this.updateConditionalValidators();
  }

  private handleSiretChange(changes: SimpleChanges): void {
    const siret = changes['siret']?.currentValue;

    if (!siret) {
      return;
    }

    this.patchCompanyFieldsFromSiret(siret);
    this.isSiretVerified = true;
  }

  private handleSiretErrorStatusChange(changes: SimpleChanges): void {
    const siretErrorStatusChange = changes['siretErrorStatus'];

    if (
      siretErrorStatusChange?.previousValue ===
        this.GATEWAY_ERROR_STATUS_NUMBER &&
      siretErrorStatusChange.currentValue === null
    ) {
      this.clearSiretField();
      this.emitDisableNextButton();
    }
  }

  private patchCompanyFieldsFromSiret(siret: Siret): void {
    this.isNameEditable = !siret.name;

    this.form.patchValue({
      name: siret.name ?? '',
      address1: siret.addressLine1,
      address2: siret.addressLine2,
      address3: siret.addressLine3,
      city: siret.city,
      postalCode: siret.postalCode,
    });

    this.updateNameValidators();
  }

  private resetAutofilledCompanyFields(): void {
    this.isNameEditable = false;
    this.form.patchValue({
      name: '',
      address1: '',
      address2: '',
      address3: '',
      city: '',
      postalCode: '',
    });
    this.updateNameValidators();
  }

  private clearSiretField(): void {
    this.form.controls.siret.setValue('');
  }

  private clearVatNumberField(): void {
    this.form.controls.vatNumber.setValue('');
  }

  private subscribeToFormChanges(): void {
    this.form.valueChanges
      .pipe(
        debounceTime(this.DEBOUNCE_TIME_TO_SHARE_FORM_IN_MS),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.updateConditionalValidators();
        this.companyChanged.emit(this.form.getRawValue());
        this.emitDisableNextButton();
      });
  }

  private emitDisableNextButton(): void {
    const isNotaire =
      this.form.controls.industryType.value === this.industryType.NOTAIRE;
    this.disableNextButton.emit(!this.form.valid || isNotaire);
  }

  private updateConditionalValidators(): void {
    this.form.controls.vatNumber.updateValueAndValidity({ emitEvent: false });
    this.form.controls.siret.updateValueAndValidity({ emitEvent: false });
  }

  private updateNameValidators(): void {
    const validators = this.isNameEditable
      ? [Validators.required, Validators.minLength(2), Validators.maxLength(50)]
      : [Validators.required, Validators.maxLength(255)];
    this.form.controls.name.setValidators(validators);
    this.form.controls.name.updateValueAndValidity({ emitEvent: false });
  }

  private getSelectedCountryFromInitialValue(): PSelectItem {
    return (
      this.countries.find((c) => c.label === this.initialValue.country) ??
      this.countries[0]
    );
  }

  private getSelectedIndustryTypeFromInitialValue(): PSelectItem {
    return (
      this.industryTypeItems.find(
        (item) => item.value === this.initialValue.industryType,
      ) ?? { value: null, label: null }
    );
  }
}
